import { Link } from "react-router-dom";
import { RefreshCw, Search, LogOut, ListChecks, UserCog, CalendarClock, Megaphone, Palette } from "lucide-react";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { STATUSES } from "./LeadCard";
import { DigestButton } from "./DigestButton";

const toolBtn = "inline-flex items-center gap-2 text-base text-slate-700 bg-white border border-slate-300 rounded-lg px-4 py-2 transition-all";

export const AdminHeader = ({ loading, onRefresh, onLogout }) => (
  <div className="flex flex-wrap items-center justify-between gap-4">
    <div>
      <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Leads Dashboard</h1>
      <p className="text-slate-600 mt-1 text-base">All quote requests, newest first.</p>
    </div>
    <div className="flex flex-wrap items-center gap-3">
      <DigestButton />
      <Link to="/admin/seo-checklist" data-testid="admin-seo-checklist-link" className={`${toolBtn} hover:border-cyan-500`}>
        <ListChecks className="w-4 h-4" /> SEO checklist
      </Link>
      <Link to="/admin/seo-plan" data-testid="admin-seo-plan-link" className={`${toolBtn} hover:border-cyan-500`}>
        <Megaphone className="w-4 h-4" /> SEO plan
      </Link>
      <Link to="/admin/brand" data-testid="admin-brand-link" className={`${toolBtn} hover:border-cyan-500`}>
        <Palette className="w-4 h-4" /> Logo files
      </Link>
      <Link to="/admin/account" data-testid="admin-account-link" className={`${toolBtn} hover:border-cyan-500`}>
        <UserCog className="w-4 h-4" /> Account
      </Link>
      <button data-testid="admin-refresh-button" onClick={onRefresh} className={`${toolBtn} hover:border-cyan-500`}>
        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
      </button>
      <button data-testid="admin-logout-button" onClick={onLogout} className={`${toolBtn} hover:border-red-400`}>
        <LogOut className="w-4 h-4" /> Sign out
      </button>
    </div>
  </div>
);

export const AdminStats = ({ stats }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
    {[["Total Leads", stats.total, "total"], ["New", stats.new, "new"], ["Contacted", stats.contacted, "contacted"]].map(([label, value, key]) => (
      <div key={key} data-testid={`admin-stat-${key}`} className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
        <div className="text-3xl font-extrabold text-cyan-700 font-mono">{value}</div>
        <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">{label}</div>
      </div>
    ))}
  </div>
);

export const AdminFilters = ({ query, setQuery, filter, setFilter, needsFollowUp, setNeedsFollowUp, followUpCount }) => (
  <div className="flex flex-wrap items-center gap-4 mt-8">
    <div className="relative flex-1 min-w-[220px]">
      <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
      <Input
        data-testid="admin-search-input"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search name, email, region, waterbody..."
        className="bg-white border-slate-300 pl-9 h-11"
      />
    </div>
    <Select value={filter} onValueChange={setFilter}>
      <SelectTrigger data-testid="admin-filter-status" className="w-[200px] bg-white border-slate-300 h-11">
        <SelectValue placeholder="Filter by status" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all" data-testid="filter-option-all">All statuses</SelectItem>
        {STATUSES.map((s) => (
          <SelectItem key={s} value={s} data-testid={`filter-option-${s}`}>{s}</SelectItem>
        ))}
      </SelectContent>
    </Select>
    <button
      type="button"
      data-testid="admin-filter-follow-up"
      aria-pressed={needsFollowUp}
      onClick={() => setNeedsFollowUp((v) => !v)}
      className={`inline-flex items-center gap-2 h-11 rounded-full border px-4 text-base font-medium transition-colors ${
        needsFollowUp ? "bg-rose-600 border-rose-600 text-white" : "bg-white border-slate-300 text-slate-700 hover:border-rose-400"
      }`}
    >
      <CalendarClock className="w-4 h-4" /> Needs follow-up
      <span
        data-testid="admin-follow-up-count"
        className={`rounded-full px-2 py-0.5 text-xs font-bold ${needsFollowUp ? "bg-white/20 text-white" : followUpCount ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-500"}`}
      >
        {followUpCount}
      </span>
    </button>
  </div>
);
